import {
  measurementProfileListSchema,
  measurementProfileSchema,
  measurementTemplateSchema,
  profileHistorySchema,
  resolvedMeasurementFieldSchema,
  type CreateProfileInput,
  type MeasurementProfile,
  type MeasurementTemplate,
  type ProfileRevision,
  type ResolvedMeasurementField,
  type UpdateProfileInput,
} from '@tailoring-crm/shared';
import { z } from 'zod';
import type { ApiClient } from '../api/client.js';

/**
 * Every measurement-profile call the app makes.
 *
 * `getCaptureForm` is the one worth naming: the capture form is not built in the browser from
 * a hardcoded list of fields, it is FETCHED. That is what makes "the form is generated entirely
 * from the template" true rather than aspirational — a shop that adds a field sees it on the
 * next capture without the app shipping again.
 */

const BASE = '/measurement-profiles';

const captureFormSchema = z.object({
  template: measurementTemplateSchema,
  fields: z.array(resolvedMeasurementFieldSchema),
});

export interface CaptureForm {
  template: MeasurementTemplate;
  fields: ResolvedMeasurementField[];
}

/**
 * Whose measurements to fetch.
 *
 * A discriminated union rather than two optional ids, so "neither" and "both" are not states the
 * caller can reach — the API refuses them, and there is no reason to let a component compose one.
 */
export type MeasurementOwner = { kind: 'customer'; customerId: string };

export function ownerQuery(owner: MeasurementOwner): Record<string, string> {
  return { customerId: owner.customerId };
}

export async function listProfiles(
  client: ApiClient,
  owner: MeasurementOwner,
): Promise<MeasurementProfile[]> {
  const { profiles } = await client.get(BASE, {
    schema: measurementProfileListSchema,
    query: ownerQuery(owner),
  });
  return profiles;
}

/**
 * The fields to render. Pass `profileId` when editing: the server then includes any field the
 * shop has since switched off that this profile already holds a value for, so an edit cannot
 * quietly drop a measurement somebody took.
 */
export async function getCaptureForm(
  client: ApiClient,
  garmentTypeKey: string,
  profileId?: string,
): Promise<CaptureForm> {
  return await client.get(`${BASE}/form`, {
    schema: captureFormSchema,
    query: { garmentTypeKey, ...(profileId ? { profileId } : {}) },
  });
}

export async function createProfile(
  client: ApiClient,
  input: CreateProfileInput,
): Promise<MeasurementProfile> {
  return await client.post(BASE, input, { schema: measurementProfileSchema });
}

export async function updateProfile(
  client: ApiClient,
  profileId: string,
  patch: UpdateProfileInput,
): Promise<MeasurementProfile> {
  return await client.patch(`${BASE}/${profileId}`, patch, { schema: measurementProfileSchema });
}

export async function deleteProfile(client: ApiClient, profileId: string): Promise<void> {
  await client.del(`${BASE}/${profileId}`);
}

/** "What did we have before?", straight out of the append-only audit log. */
export async function profileHistory(
  client: ApiClient,
  profileId: string,
): Promise<ProfileRevision[]> {
  const { revisions } = await client.get(`${BASE}/${profileId}/history`, {
    schema: profileHistorySchema,
  });
  return revisions;
}
